import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Select.
 *
 * A native <select> renders the operating system's own list — Chrome's grey
 * menu on Windows, a wheel on iOS — which is the one part of an interface you
 * cannot design. This replaces it with a listbox that belongs to the rest of
 * the app, without giving up anything a native select does:
 *
 *   - full keyboard support, including type-ahead
 *   - screen-reader semantics (combobox + listbox + option)
 *   - opens where the trigger is, scales from it, closes on Escape or outside
 *
 * Motion is deliberately restrained. A QA manager opens these dozens of times
 * an hour, so it is 140ms, ease-out, origin-aware, and gone entirely under
 * prefers-reduced-motion. It never animates from scale(0) — nothing in the real
 * world appears from nothing.
 */

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  /** Small right-aligned note, e.g. a count or a hint. */
  hint?: string;
  disabled?: boolean;
}

interface SelectProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  /** Shown when the value matches no option, or the value is ''. */
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Renders the trigger at table-filter size. */
  size?: 'sm' | 'md';
  /** Stretch to the container. Filters stay intrinsic; form fields fill. */
  block?: boolean;
  name?: string;
}

const GAP = 6;
const MAX_MENU_HEIGHT = 288;

export function Select<T extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  id,
  ariaLabel,
  disabled,
  size = 'md',
  block,
  name,
}: SelectProps<T>) {
  const reactId = useId();
  const listboxId = `${id ?? reactId}-listbox`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const typeAhead = useRef({ query: '', at: 0 });

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; drop: 'down' | 'up' } | null>(null);
  /** Drives the enter transition; separate from `open` so the first frame is the closed state. */
  const [shown, setShown] = useState(false);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const drop: 'down' | 'up' = below < Math.min(MAX_MENU_HEIGHT, options.length * 34 + 12) + GAP && r.top > below ? 'up' : 'down';
    setRect({
      top: drop === 'down' ? r.bottom + GAP : r.top - GAP,
      left: r.left,
      width: Math.max(r.width, 168),
      drop,
    });
  }, [options.length]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    place();
    setOpen(true);
    setActive(selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => !o.disabled));
  }, [disabled, place, selectedIndex, options]);

  const closeMenu = useCallback((refocus = true) => {
    setShown(false);
    setOpen(false);
    setActive(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.disabled) return;
      onChange(option.value);
      closeMenu();
    },
    [options, onChange, closeMenu],
  );

  // Start the enter transition one frame after mount, so it transitions from
  // the closed state rather than appearing already open.
  useLayoutEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Keep the active option in view during keyboard navigation.
  useEffect(() => {
    if (!open || active < 0) return;
    menuRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  // Close on outside interaction, and on any scroll that would detach the menu
  // from its trigger. Repositioning mid-scroll reads as drift; closing is honest.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu(false);
    };
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      closeMenu(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', () => closeMenu(false));
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, closeMenu]);

  const move = (delta: number) => {
    const n = options.length;
    if (!n) return;
    let next = active < 0 ? (delta > 0 ? -1 : n) : active;
    for (let i = 0; i < n; i++) {
      next = (next + delta + n) % n;
      if (!options[next]?.disabled) break;
    }
    setActive(next);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        return;
      case 'Tab':
        closeMenu(false);
        return;
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        return;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        return;
      case 'Home':
        e.preventDefault();
        setActive(options.findIndex((o) => !o.disabled));
        return;
      case 'End':
        e.preventDefault();
        setActive(options.length - 1 - [...options].reverse().findIndex((o) => !o.disabled));
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(active);
        return;
      default:
        break;
    }

    // Type-ahead: typing jumps to the first option starting with what you typed.
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = Date.now();
      const q = (now - typeAhead.current.at < 700 ? typeAhead.current.query : '') + e.key.toLowerCase();
      typeAhead.current = { query: q, at: now };
      const found = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(q));
      if (found >= 0) setActive(found);
    }
  };

  const menu =
    open && rect
      ? createPortal(
          <div
            ref={menuRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={id}
            data-open={shown ? 'true' : 'false'}
            data-drop={rect.drop}
            className="select-menu"
            style={{
              top: rect.drop === 'down' ? rect.top : undefined,
              bottom: rect.drop === 'up' ? window.innerHeight - rect.top : undefined,
              left: rect.left,
              minWidth: rect.width,
              maxHeight: MAX_MENU_HEIGHT,
            }}
          >
            {options.map((o, i) => (
              <div
                key={o.value}
                role="option"
                data-index={i}
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                className={`select-option ${i === active ? 'is-active' : ''} ${o.value === value ? 'is-selected' : ''} ${o.disabled ? 'is-disabled' : ''}`}
                onPointerEnter={() => !o.disabled && setActive(i)}
                onClick={() => commit(i)}
              >
                <span className="select-check" aria-hidden>
                  {o.value === value ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                      <path d="M2.5 6.2 4.8 8.5 9.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </span>
                <span className="select-option-label">{o.label}</span>
                {o.hint && <span className="select-option-hint">{o.hint}</span>}
              </div>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {name && <input type="hidden" name={name} value={value} />}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-activedescendant={undefined}
        disabled={disabled}
        className={`select-trigger ${size === 'sm' ? 'is-sm' : ''} ${block ? 'is-block' : ''} ${open ? 'is-open' : ''} ${!selected ? 'is-placeholder' : ''}`}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className="select-value">{selected ? selected.label : placeholder}</span>
        <svg className="select-caret" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M3.2 4.8 6 7.6l2.8-2.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {menu}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Filter chips
 *
 * Three loose outlined buttons read as "unrelated actions". A grouped set of
 * toggles reads as what it is: a filter with several independent switches.
 * ------------------------------------------------------------------ */

export function FilterChip({
  active,
  onClick,
  children,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button type="button" className={`chip ${active ? 'is-on' : ''}`} aria-pressed={active} onClick={onClick} title={title}>
      <span className="chip-dot" aria-hidden />
      {children}
    </button>
  );
}
